import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../../services/api'
import type { Event } from '../../services/api'

interface StationOperatorViewProps {
  onExitStationMode: () => void
}

interface Athlete {
  id: string
  name: string
  position?: string
  school?: string
}

interface DrillOption {
  id: string
  name: string
  unit: string
}

export default function StationOperatorView({ onExitStationMode }: StationOperatorViewProps) {
  const [events, setEvents] = useState<Event[]>([])
  const [selectedEventId, setSelectedEventId] = useState<string>('')
  const [selectedDrillId, setSelectedDrillId] = useState<string>('')
  const [drills, setDrills] = useState<DrillOption[]>([])
  const [queue, setQueue] = useState<Athlete[]>([])
  const [currentAthleteIndex, setCurrentAthleteIndex] = useState(0)
  const [resultValue, setResultValue] = useState<string>('')
  const [recording, setRecording] = useState(false)
  const [loadingEvents, setLoadingEvents] = useState(true)

  const currentAthlete = queue[currentAthleteIndex] || null
  const upcomingAthletes = queue.slice(currentAthleteIndex + 1, currentAthleteIndex + 6)
  const selectedDrill = drills.find(d => d.id === selectedDrillId)

  // Fetch active events on mount
  useEffect(() => {
    async function fetchEvents() {
      setLoadingEvents(true)
      try {
        const response = await apiService.getEvents({ status: 'ACTIVE', limit: 50 })
        if (response.data) {
          setEvents(response.data.events)
        }
      } catch {
        console.error('Failed to fetch events')
      } finally {
        setLoadingEvents(false)
      }
    }
    fetchEvents()
  }, [])

  // When event is selected, load its drills and participants
  useEffect(() => {
    if (!selectedEventId) {
      setDrills([])
      setQueue([])
      setSelectedDrillId('')
      return
    }

    async function fetchEventDetails() {
      try {
        const response = await apiService.getEvent(selectedEventId)
        if (response.data?.drills) {
          setDrills(
            response.data.drills.map(d => ({
              id: d.id,
              name: d.name,
              unit: d.unit,
            }))
          )
        }

        const participantsResponse = await apiService.getEventParticipants(selectedEventId, {
          status: 'CHECKED_IN',
        })
        if (participantsResponse.data?.participants) {
          setQueue(
            participantsResponse.data.participants.map(p => ({
              id: p.athlete.id,
              name: `${p.athlete.profile.firstName} ${p.athlete.profile.lastName}`,
              position: p.athlete.profile.position,
              school: p.school?.name,
            }))
          )
          setCurrentAthleteIndex(0)
        }
      } catch {
        console.error('Failed to fetch event details')
      }
    }
    fetchEventDetails()
  }, [selectedEventId])

  const handleRecordResult = useCallback(async () => {
    if (!currentAthlete || !selectedDrillId || !resultValue || recording) {
      return
    }

    setRecording(true)
    try {
      // POST result to the API - adjust endpoint as needed
      await apiService.getEvent(selectedEventId) // placeholder - actual result recording endpoint
      // In a real implementation this would be something like:
      // await apiService.recordResult({ eventId: selectedEventId, drillId: selectedDrillId, athleteId: currentAthlete.id, value: parseFloat(resultValue) })
      setResultValue('')
    } catch {
      console.error('Failed to record result')
    } finally {
      setRecording(false)
    }
  }, [currentAthlete, selectedDrillId, selectedEventId, resultValue, recording])

  const handleNextAthlete = useCallback(() => {
    if (currentAthleteIndex < queue.length - 1) {
      setCurrentAthleteIndex(prev => prev + 1)
      setResultValue('')
    }
  }, [currentAthleteIndex, queue.length])

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header with exit button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Station Mode</h1>
        <button
          onClick={onExitStationMode}
          className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Exit Station Mode
        </button>
      </div>

      {/* Event / Drill Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="event-select" className="block text-sm font-medium text-gray-700 mb-1">
            Event
          </label>
          <select
            id="event-select"
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            disabled={loadingEvents}
          >
            <option value="">Select an event...</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="drill-select" className="block text-sm font-medium text-gray-700 mb-1">
            Drill
          </label>
          <select
            id="drill-select"
            value={selectedDrillId}
            onChange={e => setSelectedDrillId(e.target.value)}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            disabled={!selectedEventId || drills.length === 0}
          >
            <option value="">Select a drill...</option>
            {drills.map(drill => (
              <option key={drill.id} value={drill.id}>
                {drill.name} ({drill.unit})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Current Athlete Display */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <p className="text-sm font-medium text-gray-500 mb-2">Current Athlete</p>
        {currentAthlete ? (
          <div>
            <h2 className="text-4xl font-bold text-gray-900">{currentAthlete.name}</h2>
            <div className="flex gap-3 mt-2">
              {currentAthlete.position && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {currentAthlete.position}
                </span>
              )}
              {currentAthlete.school && (
                <span className="text-sm text-gray-500">{currentAthlete.school}</span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-2xl text-gray-400">No athlete selected</p>
        )}
      </div>

      {/* Result Entry */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <p className="text-sm font-medium text-gray-500 mb-3">Result Entry</p>
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label htmlFor="result-input" className="sr-only">
              Result value
            </label>
            <input
              id="result-input"
              type="number"
              step="any"
              value={resultValue}
              onChange={e => setResultValue(e.target.value)}
              placeholder="Enter value"
              className="w-full text-2xl rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 p-3"
              disabled={!currentAthlete || !selectedDrillId}
            />
          </div>
          {selectedDrill && (
            <span className="text-lg text-gray-500 pb-3">{selectedDrill.unit}</span>
          )}
          <button
            onClick={handleRecordResult}
            disabled={!currentAthlete || !selectedDrillId || !resultValue || recording}
            className="px-8 py-3 text-lg font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {recording ? 'Recording...' : 'Record Result'}
          </button>
        </div>
      </div>

      {/* Queue and Next button */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm font-medium text-gray-500">Up Next</p>
          <button
            onClick={handleNextAthlete}
            disabled={currentAthleteIndex >= queue.length - 1}
            className="px-6 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Next Athlete
          </button>
        </div>
        {upcomingAthletes.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {upcomingAthletes.map((athlete, idx) => (
              <li key={athlete.id} className="py-3 flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                  {idx + 1}
                </span>
                <span className="text-sm font-medium text-gray-900">{athlete.name}</span>
                {athlete.position && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {athlete.position}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">No athletes in queue</p>
        )}
      </div>
    </div>
  )
}
